import { useCallback, useEffect, useRef, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { PaymentIncidentsService } from "../services/paymentIncidents.service";
import type { PaymentIncidentDetail } from "../types/paymentIncidents.types";

export interface UsePaymentIncidentResult {
  incident: PaymentIncidentDetail | null;
  isLoading: boolean;
  error: string | null;
  reload: () => void;
  /** Takes a detail an action already answered with, instead of reading it again. */
  replace: (incident: PaymentIncidentDetail) => void;
}

/**
 * One incident with its timeline, read when `id` is set and again on `reload`. An answer for an id that is no
 * longer the one asked for is dropped, so switching rows never shows the previous incident.
 */
export function usePaymentIncident(id: string | null): UsePaymentIncidentResult {
  const [state, setState] = useState<{
    id: string | null;
    incident: PaymentIncidentDetail | null;
    error: string | null;
  }>({ id: null, incident: null, error: null });
  const [reloadKey, setReloadKey] = useState(0);
  const seq = useRef(0);

  useEffect(() => {
    if (!id) return;
    const current = ++seq.current;
    PaymentIncidentsService.get(id)
      .then((incident) => {
        if (current === seq.current) setState({ id, incident, error: null });
      })
      .catch((error: unknown) => {
        if (current !== seq.current) return;
        setState((previous) => ({
          id,
          incident: previous.id === id ? previous.incident : null,
          error: HttpApiService.getErrorMessage(error),
        }));
      });
  }, [id, reloadKey]);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);
  const replace = useCallback((incident: PaymentIncidentDetail) => {
    seq.current += 1;
    setState({ id: incident.id, incident, error: null });
  }, []);

  const matches = !!id && state.id === id;
  return {
    incident: matches ? state.incident : null,
    isLoading: !!id && !matches,
    error: matches ? state.error : null,
    reload,
    replace,
  };
}
